import { useState, useEffect } from "react";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Clock, Plus, Trash2, Save, CalendarOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { useFormatDate, formatDate } from "../../utils/dateFormat";

// Reference dates for day-of-week localization (2024-01-01 was Monday)
const dayOfWeekToDate = (dow) => new Date(2024, 0, 1 + dow);

export default function OwnerAvailability() {
  const { t } = useTranslation();
  const fmt = useFormatDate();
  const [hours, setHours] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddOverride, setShowAddOverride] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [hoursRes, overridesRes] = await Promise.all([
        axios.get(`${API}/owner/opening-hours`),
        axios.get(`${API}/owner/availability-overrides`),
      ]);
      setHours(hoursRes.data);
      setOverrides(overridesRes.data);
    } catch (error) {
      console.error("Error fetching availability:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHours = async () => {
    setSaving(true);
    try {
      const payload = hours.map((h) => ({
        day_of_week: h.day_of_week,
        open_time: h.open_time,
        close_time: h.close_time,
        is_closed: h.is_closed,
      }));
      const res = await axios.put(`${API}/owner/opening-hours`, { hours: payload });
      setHours(res.data);
      toast.success(t("owner_availability:hours_saved"));
    } catch (error) {
      toast.error(t("owner_availability:hours_save_failed"));
    } finally {
      setSaving(false);
    }
  };

  const updateHour = (index, field, value) => {
    const updated = [...hours];
    updated[index] = { ...updated[index], [field]: value };
    setHours(updated);
  };

  const handleDeleteOverride = async (overrideId) => {
    try {
      await axios.delete(`${API}/owner/availability-overrides/${overrideId}`);
      setOverrides(overrides.filter((o) => o.override_id !== overrideId));
      toast.success(t("owner_availability:override_removed"));
    } catch (error) {
      toast.error(t("owner_availability:override_remove_failed"));
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse bg-muted h-64 rounded-xl" />
        <div className="animate-pulse bg-muted h-48 rounded-xl" />
      </div>
    );
  }

  return (
    <div data-testid="owner-availability-page">
      <h1 className="text-2xl font-medium text-foreground mb-6">{t("owner_availability:title")}</h1>

      {/* Opening Hours Section */}
      <div className="bg-card rounded-xl border border-border p-6 mb-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Clock className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="font-medium text-foreground">{t("owner_availability:opening_hours")}</h2>
              <p className="text-sm text-muted-foreground">{t("owner_availability:opening_hours_subtitle")}</p>
            </div>
          </div>
          <Button onClick={handleSaveHours} disabled={saving} data-testid="save-opening-hours-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {saving ? t("owner_availability:saving") : t("owner_availability:save_hours")}
          </Button>
        </div>

        <div className="space-y-3">
          {hours.map((h, idx) => (
            <div
              key={h.day_of_week}
              className={`flex items-center gap-4 p-3 rounded-lg transition-colors ${
                h.is_closed ? "bg-red-50/50 border border-red-100" : "bg-muted/30"
              }`}
              data-testid={`opening-hours-day-${h.day_of_week}`}
            >
              <span className="w-28 text-sm font-medium text-foreground">{fmt(dayOfWeekToDate(h.day_of_week), "EEEE")}</span>

              <Select
                value={h.is_closed ? "closed" : "open"}
                onValueChange={(v) => updateHour(idx, "is_closed", v === "closed")}
              >
                <SelectTrigger className="w-28" data-testid={`hours-status-${h.day_of_week}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">{t("owner_availability:open")}</SelectItem>
                  <SelectItem value="closed">{t("owner_availability:closed")}</SelectItem>
                </SelectContent>
              </Select>

              {!h.is_closed && (
                <>
                  <div className="flex items-center gap-2">
                    <Input
                      type="time"
                      value={h.open_time}
                      onChange={(e) => updateHour(idx, "open_time", e.target.value)}
                      className="w-32"
                      data-testid={`hours-open-${h.day_of_week}`}
                    />
                    <span className="text-sm text-muted-foreground">{t("common:to", { defaultValue: "to" })}</span>
                    <Input
                      type="time"
                      value={h.close_time}
                      onChange={(e) => updateHour(idx, "close_time", e.target.value)}
                      className="w-32"
                      data-testid={`hours-close-${h.day_of_week}`}
                    />
                  </div>
                </>
              )}

              {h.is_closed && (
                <span className="text-sm text-red-500 italic">{t("owner_availability:closed_all_day", { defaultValue: "Closed all day" })}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Availability Overrides / Blackout Dates */}
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <CalendarOff className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h2 className="font-medium text-foreground">{t("owner_availability:blackout_title")}</h2>
              <p className="text-sm text-muted-foreground">{t("owner_availability:blackout_subtitle")}</p>
            </div>
          </div>
          <Dialog open={showAddOverride} onOpenChange={setShowAddOverride}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-override-btn">
                <Plus className="h-4 w-4 mr-2" />
                {t("owner_availability:add_override")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("owner_availability:add_date_override")}</DialogTitle>
              </DialogHeader>
              <AddOverrideForm
                onSuccess={(newOverride) => {
                  setOverrides([...overrides, newOverride]);
                  setShowAddOverride(false);
                }}
              />
            </DialogContent>
          </Dialog>
        </div>

        {overrides.length > 0 ? (
          <div className="space-y-2">
            {overrides.map((o) => (
              <div
                key={o.override_id}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  o.is_closed ? "bg-red-50/50 border border-red-100" : "bg-muted/30"
                }`}
                data-testid={`override-${o.override_id}`}
              >
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-foreground">
                    {fmt(new Date(o.date + "T00:00:00"), "EEE, MMM d, yyyy")}
                  </span>
                  {o.is_closed ? (
                    <span className="text-xs text-red-600 font-medium bg-red-100 px-2 py-0.5 rounded-full">{t("owner_availability:closed_badge")}</span>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {o.custom_open_time} - {o.custom_close_time}
                    </span>
                  )}
                  {o.note && (
                    <span className="text-xs text-muted-foreground italic">({o.note})</span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteOverride(o.override_id)}
                  data-testid={`delete-override-${o.override_id}`}
                >
                  <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <CalendarOff className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">{t("owner_availability:no_overrides")}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function AddOverrideForm({ onSuccess }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    date: "",
    is_closed: true,
    custom_open_time: "09:00",
    custom_close_time: "23:00",
    max_bookings: "",
    note: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.date) return;
    setLoading(true);
    try {
      const payload = {
        date: form.date,
        is_closed: form.is_closed,
        custom_open_time: form.is_closed ? null : form.custom_open_time,
        custom_close_time: form.is_closed ? null : form.custom_close_time,
        max_bookings: form.max_bookings ? parseInt(form.max_bookings) : null,
        note: form.note || null,
      };
      const res = await axios.post(`${API}/owner/availability-overrides`, payload);
      toast.success(t("owner_availability:override_added"));
      onSuccess(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || t("owner_availability:override_add_failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>{t("owner_availability:date")}</Label>
        <Input
          type="date"
          value={form.date}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
          required
          className="mt-1"
          data-testid="override-date-input"
        />
      </div>

      <div>
        <Label>{t("owner_availability:type")}</Label>
        <Select
          value={form.is_closed ? "closed" : "modified"}
          onValueChange={(v) => setForm({ ...form, is_closed: v === "closed" })}
        >
          <SelectTrigger className="mt-1" data-testid="override-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="closed">{t("owner_availability:full_day_closure")}</SelectItem>
            <SelectItem value="modified">{t("owner_availability:modified_hours")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {!form.is_closed && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>{t("owner_availability:open_time")}</Label>
            <Input
              type="time"
              value={form.custom_open_time}
              onChange={(e) => setForm({ ...form, custom_open_time: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label>{t("owner_availability:close_time")}</Label>
            <Input
              type="time"
              value={form.custom_close_time}
              onChange={(e) => setForm({ ...form, custom_close_time: e.target.value })}
              className="mt-1"
            />
          </div>
        </div>
      )}

      <div>
        <Label>{t("owner_availability:note_optional")}</Label>
        <Input
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          placeholder={t("owner_availability:note_placeholder")}
          className="mt-1"
          data-testid="override-note-input"
        />
      </div>

      <Button type="submit" disabled={loading} className="w-full" data-testid="save-override-btn">
        {loading ? t("owner_availability:adding") : t("owner_availability:add_override")}
      </Button>
    </form>
  );
}
